import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

export const SITE_NAME = 'Restaurant À Ciel Ouvert';

// Share previews look best at 1200px wide. Take the widest scale up to that.
const SHARE_SCALES = ['great', 'larger', 'large'];

export interface ShareImage {
  url: string;
  width?: number;
  height?: number;
}

// Makes a backend or site-relative URL absolute on the public host. A URL on
// another host stays as it is.
// In development the backend sits behind the dev proxy. Volto does not strip
// that address, so strip it here.
export const publicUrl = (url: string): string => {
  const devBackend = (config.settings as any).devProxyToApiPath;
  const local =
    devBackend && url.startsWith(devBackend)
      ? url.slice(devBackend.length)
      : url;
  const flat = flattenToAppURL(local);
  return flat.startsWith('/') ? toPublicURL(flat) : url;
};

// Reads an image value as the content serializer returns it. The relation
// summary of `preview_image_link` gives download paths relative to `base`.
const pickScale = (image: any, base?: string): ShareImage | undefined => {
  if (!image?.download) return undefined;
  const resolve = (download: string) =>
    publicUrl(base ? `${base}/${download}` : download);
  const scale = SHARE_SCALES.map((name) => image.scales?.[name]).find(
    (item) => item?.download,
  );
  const chosen = scale || image;
  return {
    url: resolve(chosen.download),
    width: chosen.width,
    height: chosen.height,
  };
};

const fromPreviewLink = (link: any): ShareImage | undefined => {
  const target = Array.isArray(link) ? link[0] : link;
  if (!target?.['@id']) return undefined;
  const field = target.image_field || 'image';
  const image = target.image_scales?.[field]?.[0];
  if (!image) return undefined;
  return pickScale(image, image.base_path || target['@id']);
};

// Same order as Volto's own tags, then the preview image link, then the
// default image of the restaurant settings.
export const shareImage = (
  content: any,
  defaultImage?: string,
): ShareImage | undefined =>
  pickScale(content?.opengraph_image) ||
  pickScale(content?.image) ||
  pickScale(content?.preview_image) ||
  fromPreviewLink(content?.preview_image_link) ||
  (defaultImage ? { url: publicUrl(defaultImage) } : undefined);

// Same rule as the document title: "Carte · Restaurant À Ciel Ouvert", and the
// site title alone on the home page.
export const shareTitle = (pageTitle?: string, siteTitle?: string): string => {
  const separator = (config.settings as any).titleAndSiteTitleSeparator || '-';
  if (pageTitle && siteTitle && pageTitle !== siteTitle) {
    return `${pageTitle} ${separator} ${siteTitle}`;
  }
  return pageTitle || siteTitle || SITE_NAME;
};
